import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { SortValue } from '@components';
import { ALERT_STATUSES, AlertNotifications } from '@constants/alertNotifications.ts';
import { INTERNAL_SERVER_ERROR_STATUS_CODE } from '@constants/api.ts';
import type { AppDispatchFn } from '@hooks/reduxHooks.ts';
import type { MessageDescriptor } from '@lingui/core';
import { ROUTES } from '@router/constants.ts';
import { toCamelKey } from '@routes/sidebar/admin-center/___shared___/components/tables/helpers.tsx';
import {
    BASE_PAYLOAD,
    BOOLEAN_FILTER_OPTIONS,
    DRAFT_ROW_KEY,
} from '@routes/sidebar/admin-center/___shared___/constants.ts';
import type { TabsConsType } from '@routes/sidebar/admin-center/___shared___/types.ts';
import { SEARCH_DEBOUNCE_MS } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/___shared___/constants.tsx';
import { buildSortRequest } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/___shared___/utils.ts';
import {
    COMMON_VALIDATION_ERROR_LABEL,
    COMMON_VALIDATION_ERROR_MSG,
    REFERENCES_TABLE_NAMES,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/constants.ts';
import type {
    ReferencesColumn,
    ReferencesRaw,
    ReferencesValidationMSG,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/types.ts';
import { getColumnTitleWithSortIcon } from '@routes/sidebar/admin-center/tenant-client/ui/tenant-client-groupBy-table/helpers.tsx';
import { clearAlert, setAlert } from '@shared/model/alert.slice.ts';
import { renderAlertErrorDesc } from '@utils/general.tsx';
import { isEqualIgnoreCase } from '@utils/text.ts';
import { translateItem } from '@utils/translations.ts';
import type { FormInstance, Rule, RuleObject } from 'antd/es/form';
import type { ColumnType, TableProps } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { debounce, isEmpty, isNil } from 'lodash';

// ---------------------- Validation Rules ----------------------
export type RuleDefinition = {
    entityType?: string;
    fieldName?: string;
    description?: string;
    regexPattern?: string;
    requirementType?: string;
    requirementValue?: number;
    group?: string;
};

export type FieldSelector =
    | { entityType?: string; fieldName?: string; group?: string }
    | ((rule: RuleDefinition) => boolean);

type SortMap = Partial<Record<string, string>>;

type TablePaginationState = {
    pageNumber: number;
    pageSize: number;
};

type ResetTablePayloadStateParams = {
    setSort: Dispatch<SetStateAction<SortValue | null>>;
    setTableFilters: Dispatch<SetStateAction<Record<string, string[]> | null>>;
    setPagination: Dispatch<SetStateAction<TablePaginationState>>;
};

const DEFAULT_TRIGGERS: RuleObject['validateTrigger'] = ['onBlur'];

export const isEmptyInput = (value: unknown) =>
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

const normType = (t: unknown) =>
    String(t ?? '')
        .trim()
        .toLowerCase();

const SUPPORTED_REQUIREMENT_TYPES = new Set<string>([
    normType('MinLength'),
    normType('MaxLength'),
    normType('MinValue'),
    normType('MaxValue'),
    normType('RegexPattern'),
    normType('RequireLetters'),
    normType('IsValidEmail'),
    normType('IsValidPhone'),
    normType('IsValidTenantAlias'),
]);

const REGEX_VALIDATION_TYPES = new Set<string>([
    normType('RegexPattern'),
    normType('RequireLetters'),
    normType('IsValidEmail'),
    normType('IsValidPhone'),
    normType('IsValidTenantAlias'),
]);

// Build native antd min/max (and "required via min>0" behavior like before)
const pushMinMaxRule = (
    rules: Rule[],
    ruleDef: RuleDefinition | undefined,
    options: { type: 'string' | 'number'; isMin: boolean; triggers: RuleObject['validateTrigger'] },
) => {
    if (!ruleDef || !Number.isFinite(ruleDef.requirementValue)) return;

    const value = Number(ruleDef.requirementValue);
    const baseRule: Rule = {
        type: options.type,
        message: ruleDef.description,
        validateTrigger: options.triggers,
    };

    if (options.isMin) {
        rules.push({
            ...baseRule,
            required: value > 0,
            min: value,
        });
    } else {
        rules.push({
            ...baseRule,
            max: value,
        });
    }
};

/**
 * Build antd <Form.Item rules> for a single field, based on backend validation rules.
 *
 * NOTE: We only apply rules whose requirementType is in SUPPORTED_REQUIREMENT_TYPES.
 * Others are ignored (e.g. IsRequired, IsUniqueValue, etc).
 */
export const buildAntdRulesForField = (
    allRules: ReadonlyArray<RuleDefinition> | undefined,
    selector: { entityType?: string; fieldName?: string; group?: string } | ((rule: RuleDefinition) => boolean),
): Rule[] => {
    if (!allRules?.length) return [];

    // Match rules for this field:
    // - same group (for password policies)
    // - same entityType (e.g. 'Client' or 'Representative')
    // - same fieldName (e.g. 'Name', 'Email', 'Phone')
    const matchesSelector =
        typeof selector === 'function'
            ? selector
            : (r: RuleDefinition) =>
                  (selector.group ? r.group === selector.group : true) &&
                  (selector.entityType ? r.entityType === selector.entityType : true) &&
                  (selector.fieldName ? r.fieldName === selector.fieldName : true);

    const relevantRules = allRules
        .filter(matchesSelector)
        .filter((r) => SUPPORTED_REQUIREMENT_TYPES.has(normType(r.requirementType)));

    if (!relevantRules.length) return [];

    const triggers = DEFAULT_TRIGGERS;

    const rules: Rule[] = [];

    const findByType = (t: string) => relevantRules.find((r) => normType(r.requirementType) === normType(t));

    const minRuleString = findByType('MinLength');
    const maxRuleString = findByType('MaxLength');
    const minRuleNumber = findByType('MinValue');
    const maxRuleNumber = findByType('MaxValue');

    pushMinMaxRule(rules, minRuleString, { type: 'string', isMin: true, triggers });
    pushMinMaxRule(rules, maxRuleString, { type: 'string', isMin: false, triggers });
    pushMinMaxRule(rules, minRuleNumber, { type: 'number', isMin: true, triggers });
    pushMinMaxRule(rules, maxRuleNumber, { type: 'number', isMin: false, triggers });

    // Add regex-based validators (format, etc.) ONLY for REGEX_VALIDATION_TYPES
    relevantRules.forEach((r) => {
        if (!REGEX_VALIDATION_TYPES.has(normType(r.requirementType))) return;

        const raw = String(r.regexPattern ?? '').trim();
        if (!raw) return;

        let pattern: RegExp | null = null;
        try {
            pattern = new RegExp(raw);
        } catch {
            pattern = null;
        }
        if (!pattern) return;

        rules.push({
            validateTrigger: triggers,
            validator: async (_rule, value) => {
                // allow empty optional fields to pass
                if (isEmptyInput(value)) return Promise.resolve();

                if (!pattern!.test(String(value))) {
                    return Promise.reject(new Error(r.description || 'Invalid value'));
                }

                return Promise.resolve();
            },
        });
    });

    return rules;
};

// Take a form name like "BlueByteRepresentative.Phone" and return just "Phone"
const deriveFieldName = (formName: string): string | undefined => {
    const parts = String(formName).split('.').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : undefined;
};

/**
 * Build a map { [fieldNameOnForm]: antdRules[] } for multiple fields at once.
 *
 * Supports 3 calling styles:
 *  - { someFormField: { entityType: 'Client' } }
 *      -> we'll derive the backend fieldName from the last segment of "someFormField"
 *
 *  - { someFormField: { entityType: 'Representative', fieldName: 'Email' } }
 *      -> explicit mapping (overrides deriveFieldName)
 *
 *  - { passwordField: { group: 'crewPasswordPolicies' } }
 *      -> group-based policies (passwords)
 */
export const buildAntdRulesMap = <TName extends string>(
    allRules: ReadonlyArray<RuleDefinition> | undefined,
    fieldSelectors: Record<TName, FieldSelector>,
): Record<TName, Rule[]> => {
    const result = {} as Record<TName, Rule[]>;
    (Object.keys(fieldSelectors) as TName[]).forEach((formName) => {
        const selectorForField = fieldSelectors[formName];

        // Case 1: custom function selector
        if (typeof selectorForField === 'function') {
            result[formName] = buildAntdRulesForField(allRules, selectorForField);
            return;
        }

        // Case 2: group-based validation (password policy style)
        if (selectorForField.group) {
            result[formName] = buildAntdRulesForField(allRules, { group: selectorForField.group });
            // TODO: shouldn't it be like below or even this part is redundant?:
            /*
            Investigate if this case 2 is redundant or not because in many files we also pass entityType and fieldName
            So this looks like a not complete solution when we pass just group. Perhaps we can get rid of this Case 2
            completely since Case 3 covers what we want
             */
            // result[formName] = buildAntdRulesForField(allRules, selectorForField);
            return;
        }

        // Case 3: normal entityType/fieldName rules
        const fieldName = selectorForField.fieldName ? selectorForField.fieldName : deriveFieldName(String(formName));

        result[formName] = buildAntdRulesForField(allRules, {
            entityType: selectorForField.entityType,
            group: selectorForField.group,
            fieldName,
        });
    });
    return result;
};

export const normalizePasswordPolicyRules = <TRule extends { name?: string; fieldName?: string }>(
    rules: ReadonlyArray<TRule> | undefined,
) => {
    if (!rules?.length) return [];

    return rules.map((r) => {
        const raw = String(r.fieldName ?? '');
        const parts = raw.split('.').filter(Boolean);

        const group = parts.length > 1 ? parts[0] : undefined;

        // keep backend `fieldName` if it’s already clean (e.g. "MinimumLength"),
        // otherwise fallback to the last segment of dotted string
        const normalizedFieldName =
            r.fieldName && !String(r.fieldName).includes('.')
                ? r.fieldName
                : parts.length
                  ? parts[parts.length - 1]
                  : r.fieldName;

        return {
            ...r,
            group,
            fieldName: normalizedFieldName,
        };
    });
};

export const buildAdminTabPath = (
    tab: TabsConsType,
    clientId?: string | number | null,
    tenantAlias?: string | null,
) => {
    const base = `/${ROUTES.SIDEBAR.ADMIN.ROOT}`;

    if (tab === 'configuration') {
        const aliasSeg = tenantAlias ? `/${tenantAlias}` : '';

        return clientId
            ? `${base}/${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.ROOT}/${clientId}/${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.CLIENT_INFO}${aliasSeg}`
            : `${base}/${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.ROOT}/${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.CLIENT_INFO}`;
    }

    let seg = '';

    if (tab === 'subscriptions') {
        seg = ROUTES.SIDEBAR.ADMIN.SUBSCRIPTIONS;
    } else if (tab === 'matrices') {
        seg = ROUTES.SIDEBAR.ADMIN.MATRICES;
    } else if (tab === 'roles') {
        seg = ROUTES.SIDEBAR.ADMIN.ROLES;
    } else if (tab === 'analytics') {
        seg = ROUTES.SIDEBAR.ADMIN.ANALYTICS;
    } else if (tab === 'templates') {
        seg = ROUTES.SIDEBAR.ADMIN.TEMPLATES;
    }

    if (!clientId && !tenantAlias) {
        return `${base}/${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.ROOT}/${ROUTES.SIDEBAR.ADMIN.CONFIGURATION.CLIENT_INFO}`;
    }

    return `${base}/${seg}/${clientId}/${tenantAlias}`;
};

export const addFieldsErrorsToBanner = (
    errorInfo: any,
    titleMap?: Record<string, string | MessageDescriptor>,
): ReferencesValidationMSG[] => {
    const rawErrorFields = errorInfo?.errorFields ?? [];

    const fieldErrors: ReferencesValidationMSG[] = rawErrorFields
        .map((f: any) => {
            const path = f.name ?? [];
            const field = String(path[path.length - 1] ?? '');
            const messages = (f.errors ?? []).filter((e: string) => e && e.trim().length > 0);

            if (!field || messages.length === 0) return null;

            const label = translateItem(titleMap?.[field] ?? field);

            return { field, label, messages };
        })
        .filter(Boolean);

    if (!fieldErrors.length) {
        return [
            {
                field: '_',
                label: COMMON_VALIDATION_ERROR_LABEL(),
                messages: [COMMON_VALIDATION_ERROR_MSG()],
            },
        ];
    }

    return fieldErrors;
};

export const handleApiValidationError = (args: {
    err: any;
    dispatch: AppDispatchFn;
    form: { setFields: (fields: { name: any; errors: string[] }[]) => void };
    setValidationErrors: (msgs: ReferencesValidationMSG[] | null) => void;
    backendFieldsToFormFields?: Record<string, string>;
    titleMap?: Record<string, string | MessageDescriptor>;
}) => {
    const { err, dispatch, form, setValidationErrors, backendFieldsToFormFields, titleMap } = args;

    const status = err?.status ?? err?.response?.status ?? err?.data?.status;
    const statusText = err?.statusText ?? err?.response?.statusText ?? err?.data?.statusText ?? '';

    const possibleResponseErrorFields =
        err?.data?.reason?.errorFields ??
        err?.data?.errorFields ??
        err?.response?.data?.errorFields ??
        err?.errorFields ??
        [];

    if (status < INTERNAL_SERVER_ERROR_STATUS_CODE) {
        dispatch(clearAlert()); //skip banner for 4xx
    } else {
        dispatch(
            setAlert({
                type: ALERT_STATUSES.ERROR,
                message: AlertNotifications[ALERT_STATUSES.ERROR].message,
                description: renderAlertErrorDesc(status, statusText),
            }),
        );
    }

    // helper: case-insensitive mapping
    const resolveMappedField = (backendName: string) => {
        if (!backendName) return '';

        // fast path (exact)
        const direct = backendFieldsToFormFields?.[backendName];
        if (direct) return direct;

        // case-insensitive match
        const matchedEntry = backendFieldsToFormFields
            ? Object.entries(backendFieldsToFormFields).find(([key]) => isEqualIgnoreCase(key, backendName))
            : undefined;

        return matchedEntry?.[1] || (backendName ? backendName.charAt(0).toLowerCase() + backendName.slice(1) : '');
    };

    if (!isEmpty(possibleResponseErrorFields)) {
        const fieldErrors: ReferencesValidationMSG[] = possibleResponseErrorFields
            .map((f: any) => {
                const rawName = String(f?.name ?? '');
                const msg = String(f?.error ?? '').trim();
                const backendName = rawName.split('.').filter(Boolean).pop() ?? rawName;
                const formField = resolveMappedField(backendName);

                if (!formField || !msg) return null;

                const label = translateItem(titleMap?.[formField] ?? formField);
                return { field: formField, label, messages: [msg] };
            })
            .filter(Boolean);

        setValidationErrors(
            fieldErrors.length
                ? fieldErrors
                : [{ field: '_', label: COMMON_VALIDATION_ERROR_LABEL(), messages: [COMMON_VALIDATION_ERROR_MSG()] }],
        );

        form.setFields(
            fieldErrors.map((e) => ({
                name: e.field,
                errors: e.messages,
            })),
        );

        return;
    }

    const msg = String(
        err?.data?.message ??
            err?.data?.reason?.message ??
            err?.response?.data?.message ??
            COMMON_VALIDATION_ERROR_MSG(),
    );

    setValidationErrors([{ field: '_', label: COMMON_VALIDATION_ERROR_LABEL(), messages: [msg] }]);
};

export const addValidationRulesToColumns = <T extends { dataIndex: string; rules?: any }>(
    columns: T[],
    rulesMap?: Record<string, any>,
): T[] => {
    if (!rulesMap) return columns;

    return columns.map((col) => {
        const dynamicRules = rulesMap[col.dataIndex];
        if (!dynamicRules) return col;

        return {
            ...col,
            rules: dynamicRules,
        };
    });
};

// Optional Select helper:
// If user didn't touch the select -> keep original record value.
// If user touched it and cleared -> antd gives `undefined`, convert to `null` for API.
// If user touched it and selected -> use the new value.
export const getOptionalSelectValue = (form: FormInstance, field: string, values: any, record: any) => {
    const touched = form.isFieldTouched(field);

    if (!touched) {
        // user didn't interact -> keep existing value
        return record[field] ?? null;
    }

    // user interacted -> undefined means "cleared"
    return values[field] ?? null;
};

// Input helper method.
// If user didn't touch the input -> keep original record value.
// If user touched it and cleared -> and gives `undefined`, null or empty string, convert to `null` for API.
export const getInputNullableValue = (form: FormInstance, field: string, values: any, record: any) => {
    const touched = form.isFieldTouched(field);

    if (!touched) {
        // user didn't interact -> keep existing value
        return isEmptyInput(record[field]) ? null : record[field];
    }

    // user interacted -> undefined, nulll or empty string converted to null
    return isEmptyInput(values[field]) ? null : values[field];
};

// Pass `t` from useLingui() as `translate` and list `t` in the memo deps, so titles follow a language switch without a reload.
export const mapFieldsToColumn = (
    c: ReferencesRaw,
    translate: (descriptor: MessageDescriptor) => string = translateItem,
): ReferencesColumn => {
    const dataIndex = c.dataIndex ?? (typeof c.title === 'string' ? toCamelKey(c.title) : c.title.id);
    return {
        ...c,
        title: typeof c.title === 'string' ? c.title : translate(c.title),
        dataIndex,
        key: dataIndex,
        editable: c.editable,
        require: c.require,
        type: c.type,
        dataSource: c.dataSource ?? null,
        source: (c as any).source ?? REFERENCES_TABLE_NAMES.RANKS, // default will be ranks
        width: c.width ?? undefined,
    };
};

export const createDebouncedTextSearch = (
    dispatch: AppDispatchFn,
    actionCreator: (args: any) => any,
    clientId: number,
    tenantAlias: string,
    isClientIdAndTenantAliasAvailable: boolean,
    sortMap: SortMap,
    wait: number = SEARCH_DEBOUNCE_MS,
    rankSetId?: number, // extra param just for ranks table
) =>
    debounce((value: string, sortState?: SortValue | null, filters?: Record<string, string[]>) => {
        if (!isClientIdAndTenantAliasAvailable) return;

        const { sortBy, sortOrder } = buildSortRequest(sortState ?? null, sortMap);

        const mappedFilters = filters
            ? Object.entries(filters).reduce<Record<string, string[]>>((acc, [uiKey, vals]) => {
                  const clean = (vals ?? []).map(String).filter(Boolean);
                  if (!clean.length) return acc;

                  const backendKey = sortMap[String(uiKey)] ?? String(uiKey);
                  acc[backendKey] = clean;
                  return acc;
              }, {})
            : null;

        void dispatch(
            actionCreator({
                clientId,
                tenantAlias,
                ...(!isNil(rankSetId) ? { rankSetId } : {}),
                payload: {
                    pageNumber: null,
                    pageSize: null,
                    textSearch: value,
                    sortBy,
                    sortOrder,
                    filters: mappedFilters && Object.keys(mappedFilters).length ? mappedFilters : null,
                },
            }),
        );
    }, wait);

export const createOnTableChange =
    <R extends { key: React.Key }>(opts: {
        setSort: (s: SortValue) => void;
        getSort: () => SortValue | null;
        getSearchValue: () => string;
        setSearchValue: (v: string) => void;
        debouncedSearch: (value: string, sort: SortValue | null, filters?: Record<string, string[]>) => void;

        onBeforeChange?: () => void;
        onFilterChange?: (filters: Record<string, string[]> | null) => void;
    }): TableProps<R>['onChange'] =>
    (_pag, filters, sorter, extra) => {
        // add: backend paginate here, as default we have 1000 records using null as default
        if (extra?.action === 'paginate') return;

        opts.onBeforeChange?.();

        const normalizedFilters = filters
            ? Object.entries(filters).reduce<Record<string, string[]>>((acc, [k, v]) => {
                  const arr = (v ?? []).map(String).filter(Boolean);
                  if (arr.length) acc[String(k)] = arr;
                  return acc;
              }, {})
            : {};

        const nextTableFilters = Object.keys(normalizedFilters).length ? normalizedFilters : null;
        const filtersArg = nextTableFilters || undefined;

        if (extra?.action === 'filter') {
            opts.onFilterChange?.(nextTableFilters);
            opts.setSearchValue('');
            opts.debouncedSearch('', opts.getSort(), filtersArg);
            return;
        }

        const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;

        if (!sorterResult?.order) {
            const clearedSort: SortValue = { columnId: null, order: null };
            opts.setSort(clearedSort);
            opts.debouncedSearch(opts.getSearchValue(), clearedSort, filtersArg);
            return;
        }

        const normalizedOrder: SortValue['order'] =
            sorterResult.order === 'descend' ? 'desc' : sorterResult.order === 'ascend' ? 'asc' : null;

        const columnId = String(sorterResult.field ?? sorterResult.columnKey ?? '');

        const nextSortState: SortValue = { columnId, order: normalizedOrder };

        opts.setSort(nextSortState);
        opts.debouncedSearch(opts.getSearchValue(), nextSortState, filtersArg);
    };

export const addSortableColumn = <T>(col: ColumnType<T>, sort: SortValue | null, sortMap: SortMap): ColumnType<T> => {
    const columnKey = String(col.key ?? '');
    const sortField = sortMap[columnKey];
    const isSortable = Boolean((col as any).sorter && sortField);

    if (!isSortable) return col;

    const sortOrderForCol: SortOrder | undefined =
        sort && sort.columnId === columnKey ? (sort.order === 'asc' ? 'ascend' : 'descend') : undefined;

    return {
        ...col,
        sorter: (col as any).sorter,
        sortDirections: ['ascend', 'descend'] as SortOrder[],
        sortOrder: sortOrderForCol,
        title: getColumnTitleWithSortIcon(col.title as string, columnKey, sort),
    };
};

/** Table column filters for boolean columns. */
export const getBooleanTableFilters = () =>
    BOOLEAN_FILTER_OPTIONS.map(({ value, label }) => ({
        value,
        text: translateItem(label),
    }));

const isBooleanColumnValue = (value: unknown): boolean => {
    if (typeof value === 'boolean') return true;
    const normalized = String(value).trim().toLowerCase();
    return normalized === 'true' || normalized === 'false';
};

const isBooleanColumnData = (data: any[], key: string): boolean => {
    const values = (data ?? []).map((item) => item?.[key]).filter((v) => !isNil(v) && String(v).trim() !== '');

    return values.length > 0 && values.every(isBooleanColumnValue);
};

export const addFilterData = <T>(
    col: ColumnType<T>,
    data: any[],
    sortMap: SortMap,
    mapLabel?: (value: any) => string,
): ColumnType<T> => {
    const columnKey = String(col.key ?? col.dataIndex ?? '');
    const key = sortMap[columnKey] ?? columnKey;

    if (!mapLabel && isBooleanColumnData(data, key)) {
        return {
            ...col,
            filters: getBooleanTableFilters(),
        };
    }

    const options = Array.from(
        new Set(
            (data ?? [])
                .map((item) => item?.[key])
                .filter((v) => !isNil(v) && String(v).trim() !== '')
                .map(String),
        ),
    ).map((v) => ({ text: mapLabel ? mapLabel(v) : v, value: v }));

    return {
        ...col,
        filters: options,
    };
};

const getFilterRowId = (item: { id?: string | number; key?: string | number }) => String(item.id ?? item.key ?? '');

// keeps a cached collection in state by appending only new items based on id
// used e.g. to show full filters list, not only selected ones when table is not using a common useTableColumns method
// Existing ids are updated in place so create/edit refresh filter values.
export const appendUniqueItemsById = <T extends { id?: string | number; key?: string | number }>(
    items: T[] | null | undefined,
    setState: Dispatch<SetStateAction<T[]>>,
) => {
    if (!items || isEmpty(items)) return;

    setState((prevItems) => {
        if (isEmpty(prevItems)) {
            return items.filter((item) => {
                const itemId = getFilterRowId(item);
                return Boolean(itemId) && itemId !== DRAFT_ROW_KEY;
            });
        }

        const nextItems = [...prevItems];
        let hasChanges = false;

        items.forEach((item) => {
            const itemId = getFilterRowId(item);

            if (!itemId || itemId === DRAFT_ROW_KEY) return;

            const existingIndex = nextItems.findIndex((cachedItem) => getFilterRowId(cachedItem) === itemId);

            if (existingIndex === -1) {
                nextItems.push(item);
                hasChanges = true;
                return;
            }

            nextItems[existingIndex] = item;
            hasChanges = true;
        });

        return hasChanges ? nextItems : prevItems;
    });
};

export const removeUniqueItemsById = <T extends { id?: string | number; key?: string | number }>(
    ids: Array<string | number> | null | undefined,
    setState: Dispatch<SetStateAction<T[]>>,
) => {
    if (!ids || isEmpty(ids)) return;

    const idsToRemove = new Set(ids.map(String));

    setState((prevItems) => {
        const nextItems = prevItems.filter((item) => !idsToRemove.has(getFilterRowId(item)));

        return nextItems.length === prevItems.length ? prevItems : nextItems;
    });
};

export const buildPayloadWithSearchOnly = (searchValue: string) => ({
    ...BASE_PAYLOAD,
    textSearch: searchValue,
});

// search value is intentionally not cleared as it was decided (BBL-2161) to keep it after save/update operations
export const clearPayloadRelatedStateData = ({
    setSort,
    setTableFilters,
    setPagination,
}: ResetTablePayloadStateParams) => {
    setSort(null);
    setTableFilters(null);
    setPagination({ pageNumber: 1, pageSize: 10 });
};
